import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DollarSign, Percent, Calendar, TrendingUp, LogOut, Loader2, Clock, FileText, Download, Mail, Send, CheckCircle, Settings, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import CapitalRequestModal from '@/components/CapitalRequestModal';
import AccountSettingsModal from '@/components/AccountSettingsModal';
import { computeTWR, parsePerformanceHistory } from '@/utils/twrCalculation';

interface K1Document {
  name: string;
  path: string;
  uploadedAt: string;
  taxYear: string;
}

interface PartnerAccount {
  id: string;
  current_value: number;
  total_invested: number;
  ytd_return_pct: number;
  inception_date: string;
  performance_history: Array<{ date: string; value: number; change_pct?: number }>;
  last_updated: string;
  onboarding_status: string;
  k1_documents: K1Document[];
  k1_request_status: string | null;
  k1_request_date: string | null;
  capital_request_type: string | null;
  capital_request_amount: number | null;
  capital_request_status: string | null;
  capital_request_date: string | null;
  display_name: string | null;
  display_email: string | null;
}

// Helper to parse K1 documents from JSON
const parseK1Documents = (docs: unknown): K1Document[] => {
  if (!docs || !Array.isArray(docs)) return [];
  return docs.filter((doc): doc is K1Document =>
    typeof doc === 'object' && doc !== null && 'name' in doc && 'path' in doc
  );
};

const PartnerDashboard = () => {
  const { user, role, isLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [account, setAccount] = useState<PartnerAccount | null>(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [profile, setProfile] = useState<{ full_name: string; email: string } | null>(null);
  const [isRequestingK1, setIsRequestingK1] = useState(false);
  const [isCapitalRequestModalOpen, setIsCapitalRequestModalOpen] = useState(false);
  const [isAccountSettingsModalOpen, setIsAccountSettingsModalOpen] = useState(false);

  // Redirect if not logged in or not a partner
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/partner-login');
      } else if (role && role !== 'partner') {
        navigate('/partner-login');
      }
    }
  }, [user, role, isLoading, navigate]);

  // Fetch partner account data
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!user) return;

      try {
        // Fetch profile
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', user.id)
          .single();

        if (profileData) {
          setProfile(profileData);
        }

        // Fetch partner account
        const { data: accountData, error } = await supabase
          .from('partner_accounts')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching account:', error);
        } else if (accountData) {
          // Check onboarding status and redirect if needed
          if (accountData.onboarding_status === 'pending_onboarding') {
            navigate('/partner-onboarding');
            return;
          }

          setAccount({
            ...accountData,
            performance_history: Array.isArray(accountData.performance_history) 
              ? accountData.performance_history as Array<{ date: string; value: number; change_pct?: number }>
              : [],
            k1_documents: parseK1Documents(accountData.k1_documents),
            k1_request_status: accountData.k1_request_status,
            k1_request_date: accountData.k1_request_date,
            capital_request_type: accountData.capital_request_type,
            capital_request_amount: accountData.capital_request_amount,
            capital_request_status: accountData.capital_request_status,
            capital_request_date: accountData.capital_request_date,
            display_name: accountData.display_name,
            display_email: accountData.display_email,
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingAccount(false);
      }
    };

    if (user && role === 'partner') {
      fetchAccountData();
    }
  }, [user, role, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/partner-login');
  };

  const handleRequestK1 = async () => {
    if (!user || !account) return;
    
    setIsRequestingK1(true);
    try {
      const { error } = await supabase
        .from('partner_accounts')
        .update({
          k1_request_status: 'pending',
          k1_request_date: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setAccount({
        ...account,
        k1_request_status: 'pending',
        k1_request_date: new Date().toISOString(),
      });

      toast({
        title: "K1 Request Submitted",
        description: "The fund manager has been notified of your Schedule K-1 request.",
      });
    } catch (error: any) {
      console.error('Error requesting K1:', error);
      toast({
        title: "Error",
        description: "Failed to submit K1 request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRequestingK1(false);
    }
  };

  const handleCapitalRequestComplete = async () => {
    // Refetch account data
    if (!user) return;
    const { data: accountData } = await supabase
      .from('partner_accounts')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (accountData) {
      setAccount({
        ...accountData,
        performance_history: Array.isArray(accountData.performance_history) 
          ? accountData.performance_history as Array<{ date: string; value: number; change_pct?: number }>
          : [],
        k1_documents: parseK1Documents(accountData.k1_documents),
        k1_request_status: accountData.k1_request_status,
        k1_request_date: accountData.k1_request_date,
        capital_request_type: accountData.capital_request_type,
        capital_request_amount: accountData.capital_request_amount,
        capital_request_status: accountData.capital_request_status,
        capital_request_date: accountData.capital_request_date,
        display_name: accountData.display_name,
        display_email: accountData.display_email,
      });
    }
  };

  const handleViewK1 = async (doc: K1Document) => {
    try {
      const { data } = await supabase.storage
        .from('k1-documents')
        .createSignedUrl(doc.path, 60);
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      } else {
        toast({
          title: "Error",
          description: "Failed to open document.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error viewing K1:', error);
      toast({
        title: "Error",
        description: "Failed to open document.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadK1 = async (doc: K1Document) => {
    try {
      const { data } = await supabase.storage
        .from('k1-documents')
        .createSignedUrl(doc.path, 60);
      
      if (data?.signedUrl) {
        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast({
          title: "Error",
          description: "Failed to download document.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error downloading K1:', error);
      toast({
        title: "Error", 
        description: "Failed to download document.",
        variant: "destructive",
      });
    }
  };

  const handleContactManager = () => {
    window.location.href = 'mailto:cking@del-phi.com?subject=Partner Inquiry';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const daysSinceInception = account?.inception_date
    ? Math.floor((new Date().getTime() - new Date(account.inception_date).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  if (isLoading || !user) {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
        </main>
      </PageLayout>
    );
  }

  // Show pending activation message
  if (account?.onboarding_status === 'onboarding_complete') {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow">
          <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
              <div>
              <h1 className="text-3xl font-bold text-delphi-light">
                Welcome{account?.display_name ? `, ${account.display_name}` : (profile?.full_name ? `, ${profile.full_name}` : '')}
              </h1>
                <p className="text-delphi-light/60 mt-1">Partner Dashboard</p>
              </div>
              <Button 
                variant="outline" 
                className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                onClick={handleSignOut}
              >
                <LogOut size={16} className="mr-2" />
                Sign Out
              </Button>
            </div>

            <Card className="bg-delphi-dark/80 border-delphi-gray/20 max-w-2xl mx-auto">
              <CardContent className="py-12 text-center">
                <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-delphi-light mb-4">Account Pending Activation</h2>
                <p className="text-delphi-light/70 mb-6">
                  Thank you for completing your onboarding! Your information has been submitted and is currently under review by the fund manager.
                </p>
                <p className="text-delphi-light/50 text-sm">
                  You will receive an email notification once your account has been activated.
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="pt-20 flex-grow">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-delphi-light">
                Welcome{account?.display_name ? `, ${account.display_name}` : (profile?.full_name ? `, ${profile.full_name}` : '')}
              </h1>
              <p className="text-delphi-light/60 mt-1">Partner Dashboard</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                onClick={() => setIsAccountSettingsModalOpen(true)}
              >
                <Settings size={16} className="mr-2" />
                Settings
              </Button>
              <Button 
                variant="outline" 
                className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                onClick={handleSignOut}
              >
                <LogOut size={16} className="mr-2" />
                Sign Out
              </Button>
            </div>
          </div>

          {isLoadingAccount ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
            </div>
          ) : !account ? (
            <Card className="bg-delphi-dark/80 border-delphi-gray/20">
              <CardContent className="py-12 text-center">
                <p className="text-delphi-light/70">
                  Your account is being set up. Please check back soon or contact support.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                {/* Current Value Card */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5">
                  <CardContent className="py-6 px-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <DollarSign size={18} className="text-delphi-red" />
                      <span className="text-sm md:text-base text-delphi-light/70 font-medium">Current Value</span>
                    </div>
                    <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-delphi-light mb-3">
                      {formatCurrency(account.current_value)}
                    </p>
                    <div className="space-y-1 text-sm text-delphi-light/60">
                      <p>Total Gain: <span className={`font-semibold ${account.current_value - account.total_invested >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(account.current_value - account.total_invested)}
                      </span></p>
                      <p>Return: <span className={`font-semibold ${account.current_value - account.total_invested >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {account.total_invested > 0 ? formatPercent(((account.current_value - account.total_invested) / account.total_invested) * 100) : '0.00%'}
                      </span></p>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Invested Card */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5">
                  <CardContent className="py-6 px-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <TrendingUp size={18} className="text-delphi-red" />
                      <span className="text-sm md:text-base text-delphi-light/70 font-medium">Total Invested</span>
                    </div>
                    <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-delphi-light mb-3">
                      {formatCurrency(account.total_invested)}
                    </p>
                    <div className="space-y-1 text-sm text-delphi-light/60">
                      <p>Initial Capital</p>
                      <p>Inception: <span className="font-semibold text-delphi-light/80">
                        {new Date(account.inception_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span></p>
                    </div>
                  </CardContent>
                </Card>

                {/* YTD Return Card */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5">
                  <CardContent className="py-6 px-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Percent size={18} className="text-delphi-red" />
                      <span className="text-sm md:text-base text-delphi-light/70 font-medium">YTD Return</span>
                    </div>
                    {(() => {
                      const twrPct = computeTWR(
                        parsePerformanceHistory(account.performance_history),
                        account.current_value,
                        account.total_invested
                      );
                      return (
                        <p className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-3 ${twrPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatPercent(twrPct)}
                        </p>
                      );
                    })()}
                    <div className="space-y-1 text-sm text-delphi-light/60">
                      <p>Year-to-Date Performance</p>
                      <p>Updated: <span className="font-semibold text-delphi-light/80">
                        {account.last_updated ? new Date(account.last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                      </span></p>
                    </div>
                  </CardContent>
                </Card>

                {/* Days Active Card */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5">
                  <CardContent className="py-6 px-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Calendar size={18} className="text-delphi-red" />
                      <span className="text-sm md:text-base text-delphi-light/70 font-medium">Days Active</span>
                    </div>
                    <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-delphi-light mb-3">
                      {daysSinceInception}
                    </p>
                    <div className="space-y-1 text-sm text-delphi-light/60">
                      <p>Since {new Date(account.inception_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      <p>Months: <span className="font-semibold text-delphi-light/80">
                        {Math.floor(daysSinceInception / 30)}
                      </span></p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Performance Chart */}
              <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5 mb-8">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-delphi-light">Portfolio Performance</CardTitle>
                  <CardDescription className="text-delphi-light/60 text-base">
                    Track your account value over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {account.performance_history.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={account.performance_history}
                          margin={{ top: 5, right: 30, bottom: 5, left: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#666" 
                            tick={{ fill: '#999', fontSize: 12 }}
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis
                            stroke="#666"
                            tick={{ fill: '#999', fontSize: 12 }}
                            tickFormatter={(value) => formatCurrency(value)}
                            domain={(() => {
                              const values = account.performance_history.map(d => d.value);
                              const min = Math.min(...values);
                              const max = Math.max(...values);
                              const range = max - min;
                              const padding = range * 0.1 || max * 0.05;
                              return [Math.floor((min - padding) / 1000) * 1000, Math.ceil((max + padding) / 1000) * 1000];
                            })()}
                          />
                          <Tooltip
                            contentStyle={{ background: '#1a1a1a', border: '1px solid #333' }}
                            labelStyle={{ color: '#fff' }}
                            formatter={(value: number) => [formatCurrency(value), 'Portfolio Value']}
                            labelFormatter={(label) => new Date(label).toLocaleString()}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#ea384c" 
                            strokeWidth={3}
                            dot={false}
                            activeDot={{ r: 6, stroke: "#ea384c", strokeWidth: 2, fill: "#fff" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-80 flex items-center justify-center text-delphi-light/50">
                      <p className="text-lg">Performance data will appear here once trading begins.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tax Documents & Contact Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Tax Documents (K1s) */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                  <CardHeader>
                    <CardTitle className="text-xl text-delphi-light flex items-center">
                      <FileText size={20} className="mr-2 text-delphi-red" />
                      Tax Documents (Schedule K-1)
                    </CardTitle>
                    <CardDescription>
                      View and download your annual Schedule K-1 tax documents
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {account.k1_documents.length > 0 ? (
                      <div className="space-y-3">
                        {account.k1_documents.map((doc, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-delphi-red" />
                              <div>
                                <span className="text-delphi-light text-sm font-medium">{doc.name}</span>
                                {doc.taxYear && (
                                  <p className="text-delphi-light/50 text-xs">Tax Year: {doc.taxYear}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                onClick={() => handleViewK1(doc)}
                              >
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                                onClick={() => handleDownloadK1(doc)}
                              >
                                <Download size={14} className="mr-1" />
                                Download
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <FileText className="w-12 h-12 text-delphi-light/30 mx-auto mb-3" />
                        <p className="text-delphi-light/50 mb-4">
                          No tax documents available yet.
                        </p>
                        {account.k1_request_status === 'pending' ? (
                          <div className="flex items-center justify-center text-yellow-500 text-sm">
                            <Clock size={16} className="mr-2" />
                            K-1 request submitted on {account.k1_request_date ? new Date(account.k1_request_date).toLocaleDateString() : 'recently'}
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                            onClick={handleRequestK1}
                            disabled={isRequestingK1}
                          >
                            {isRequestingK1 ? (
                              <Loader2 size={14} className="mr-2 animate-spin" />
                            ) : (
                              <Send size={14} className="mr-2" />
                            )}
                            Request Schedule K-1
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Capital Requests */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                  <CardHeader>
                    <CardTitle className="text-xl text-delphi-light flex items-center">
                      <DollarSign size={20} className="mr-2 text-delphi-red" />
                      Capital Requests
                    </CardTitle>
                    <CardDescription>
                      Request deposits or withdrawals from your account
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {account.capital_request_status === 'pending' ? (
                      <div className="text-center py-6">
                        <Clock className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                        <p className="text-delphi-light font-medium mb-2">
                          {account.capital_request_type === 'deposit' ? 'Deposit' : 'Withdrawal'} Request Pending
                        </p>
                        <p className="text-delphi-light/50 text-sm mb-2">
                          Amount: ${account.capital_request_amount?.toLocaleString()}
                        </p>
                        <p className="text-delphi-light/40 text-xs">
                          Submitted: {account.capital_request_date ? new Date(account.capital_request_date).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-delphi-light/70 mb-4">
                          Need to add or withdraw funds from your account? Submit a request below.
                        </p>
                        <Button
                          className="bg-delphi-red hover:bg-red-600"
                          onClick={() => setIsCapitalRequestModalOpen(true)}
                        >
                          <ArrowUpCircle size={16} className="mr-2" />
                          Request Deposit / Withdrawal
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Contact Manager */}
                <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
                  <CardHeader>
                    <CardTitle className="text-xl text-delphi-light flex items-center">
                      <Mail size={20} className="mr-2 text-delphi-red" />
                      Contact Fund Manager
                    </CardTitle>
                    <CardDescription>
                      Have questions about your account or investments?
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-6">
                      <p className="text-delphi-light/70 mb-4">
                        Our team is here to help with any questions or concerns regarding your partnership account.
                      </p>
                      <Button
                        className="bg-delphi-red hover:bg-red-600"
                        onClick={handleContactManager}
                      >
                        <Mail size={16} className="mr-2" />
                        Email Fund Manager
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Last Updated */}
              <div className="text-center text-delphi-light/50 text-sm">
                Last updated: {account.last_updated ? new Date(account.last_updated).toLocaleString() : 'N/A'}
              </div>
            </>
          )}
        </div>

        {/* Capital Request Modal */}
        {account && user && (
          <CapitalRequestModal
            isOpen={isCapitalRequestModalOpen}
            onClose={() => setIsCapitalRequestModalOpen(false)}
            userId={user.id}
            currentValue={account.current_value}
            onRequestComplete={handleCapitalRequestComplete}
          />
        )}

        {/* Account Settings Modal */}
        {profile && (
          <AccountSettingsModal
            isOpen={isAccountSettingsModalOpen}
            onClose={() => setIsAccountSettingsModalOpen(false)}
            currentEmail={profile.email}
          />
        )}
      </main>
    </PageLayout>
  );
};

export default PartnerDashboard;